import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'

import { ArrowBackIcon, ChevronDownIcon, NoIcon, YesIcon } from '@dfds-frontend/navigator-icons/icons'
import { Counter, Switch } from '@dfds-pax/shared-components/client'
import { getVehicleResourceKey } from '@dfds-pax/utils'

import { useRoutes } from '../../Hooks/routes/useRoutes'
import { useWindowWidth2 } from '../../Hooks/useWindowWidth2'
import { useVehicles } from '../../Hooks/vehicles/useVehicles'

import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { handleListNavigation } from '../../Utils/list-keyboard'
import { isMultipleVehiclesSupportedWithRoute, isMultipleVehiclesSupportedWithVehicleType } from '../../Utils/vehicles'
import Dimensions from './Dimensions'
import { TrailerIcon, VehicleIcon } from './VehicleIcon'

import { AvailableTrailer } from '../../Hooks/vehicles/vehicle-types'

interface Props {
  onCheckMultipleVehicle: (checked: boolean) => void
  onSelectCategory: (categoryCode: string | undefined) => void
  selectedCategoryCode: string | undefined
}

const Selector: React.FC<Props> = ({ onCheckMultipleVehicle, onSelectCategory, selectedCategoryCode }) => {
  const [showTrailerSelector, setShowTrailerSelector] = useState<boolean>(false)
  const [selectedTrailerNoLongerAvailable, setSelectedTrailerNoLongerAvailable] = useState<AvailableTrailer | undefined>(undefined)
  const trailerDropdownRef = useRef<HTMLButtonElement>(null)
  const restoreTrailerFocusRef = useRef(false)
  const pendingTrailerFocusRef = useRef<'first' | 'last' | null>(null)

  const focusTrailerOption = (position: 'first' | 'last') => {
    const options = document.querySelectorAll<HTMLElement>('#trailer-options [data-list-option]')
    ;(position === 'first' ? options[0] : options[options.length - 1])?.focus()
  }

  const closeTrailerSelector = () => {
    restoreTrailerFocusRef.current = true
    setShowTrailerSelector(false)
  }

  useEffect(() => {
    if (!showTrailerSelector) {
      if (!restoreTrailerFocusRef.current) return
      restoreTrailerFocusRef.current = false
      trailerDropdownRef.current?.focus()
      return
    }
    if (!pendingTrailerFocusRef.current) return
    focusTrailerOption(pendingTrailerFocusRef.current)
    pendingTrailerFocusRef.current = null
  }, [showTrailerSelector])

  const { isMobile, isTablet } = useWindowWidth2()

  const { t } = useTranslation()
  const { selectedVehicle, vehicleApiData } = useVehicles()
  const { selectedRoute } = useRoutes()

  const {
    dispatch,
    state: { multipleVehiclesChecked, multipleVehiclesSupported },
  } = useTravelSearchContext()

  const availableTrailers = vehicleApiData?.vehicleTypes?.find((x: any) => x.code === selectedVehicle.code)?.availableTrailers || []
  const vehicleTypesInCategory = vehicleApiData.vehicleTypes?.filter((vehicleType) => vehicleType.categoryCode === selectedCategoryCode)

  // Roving tabindex: each list is a single tab stop anchored on the selected option.
  const rovingVehicleIndex = Math.max(
    0,
    vehicleTypesInCategory?.findIndex((vehicleType) => vehicleType.code === selectedVehicle?.code) ?? 0
  )
  const rovingTrailerIndex = selectedVehicle.trailer
    ? availableTrailers.findIndex((trailer) => trailer.code === selectedVehicle.trailer?.code) + 1
    : 0

  function onSelectVehicle(vehicleCode: string, count: number, categoryCode?: string, selectedTrailer?: AvailableTrailer) {
    const availableTrailers = vehicleApiData?.vehicleTypes.find((x: any) => x.code === vehicleCode)?.availableTrailers || []
    let trailer = selectedVehicle?.trailer
    if (availableTrailers?.length > 0 && !availableTrailers.some((x) => x.code === selectedVehicle?.trailer?.code)) {
      setSelectedTrailerNoLongerAvailable(selectedVehicle?.trailer)
      trailer = undefined
    } else setSelectedTrailerNoLongerAvailable(undefined)
    dispatch({
      type: 'SET_VEHICLE',
      vehicle: {
        categoryCode: categoryCode,
        code: vehicleCode,
        count: count,
        trailer: trailer ?? selectedTrailer,
      },
    })

    if (selectedRoute?.routeCode) {
      const isMultipleVehiclesSupportedWithVehicle = isMultipleVehiclesSupportedWithVehicleType(vehicleCode)
      const isMultipleVehiclesSupportedWithSelectedRoute = isMultipleVehiclesSupportedWithRoute(selectedRoute?.routeCode)
      dispatch({
        supported: isMultipleVehiclesSupportedWithVehicle && isMultipleVehiclesSupportedWithSelectedRoute,
        type: 'SET_MULTIPLE_VEHICLES_SUPPORTED',
      })
    }
  }

  function onSelectTrailer(trailer: AvailableTrailer | undefined) {
    setSelectedTrailerNoLongerAvailable(undefined)
    dispatch({
      type: 'SET_VEHICLE',
      vehicle: {
        categoryCode: selectedVehicle?.categoryCode,
        code: selectedVehicle?.code,
        count: selectedVehicle?.count ?? 1,
        trailer: trailer,
      },
    })
  }

  useEffect(() => {
    if (!multipleVehiclesSupported && selectedVehicle?.code !== 'NCAR') {
      if (selectedVehicle?.code) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        onSelectVehicle(selectedVehicle.code, 1, selectedVehicle.categoryCode, selectedVehicle.trailer)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multipleVehiclesSupported])
  return (
    <>
      {vehicleTypesInCategory?.length > 0 ? (
        multipleVehiclesChecked && multipleVehiclesSupported ? (
          <>
            <button
              className="flex w-full cursor-pointer flex-row items-center gap-semantic-global-xs border-0 border-b border-solid border-semantic-global-border-light bg-semantic-global-background-white p-semantic-global-md text-left focus-visible:outline-2 focus-visible:-outline-offset-2! focus-visible:outline-semantic-global-primary-default"
              data-e2e="vehicle-categories-selector"
              onClick={() => onSelectCategory(undefined)}
              type="button"
            >
              <ArrowBackIcon className="text-[24px] text-semantic-global-text-default" size="lg" />
              <span className="cursor-pointer text-base font-medium text-semantic-global-text-default">{t('VEHICLE-CATEGORIES')}</span>
            </button>
            <ul className="m-0 list-none border-0 border-solid pl-0" data-travel-search-child>
              {vehicleTypesInCategory.map((vehicleType) => {
                const count = selectedVehicle?.code === vehicleType.code ? (selectedVehicle?.count ?? 1) : 0
                const downDisabled = count === 0 || selectedVehicle?.code !== vehicleType.code
                const upDisabled =
                  (!!selectedVehicle &&
                    selectedVehicle.count > 0 &&
                    selectedVehicle.categoryCode === vehicleType.categoryCode &&
                    selectedVehicle.code !== vehicleType.code) ||
                  count === 9
                return (
                  <li
                    className="m-0 flex min-h-[90px] list-none items-center gap-semantic-global-md border-0 border-b border-solid border-semantic-global-border-light p-semantic-global-md"
                    data-e2e={`vehicle-type-${vehicleType.code}`}
                    key={vehicleType.code}
                  >
                    <div className="flex flex-col">
                      <label className="text-base font-medium text-semantic-global-text-default">
                        {t(getVehicleResourceKey(vehicleType.code))}
                      </label>
                      <div className="text-sm text-semantic-global-text-light">{t(`VEHICLE-DESCRIPTION-${vehicleType.code}`)}</div>
                    </div>
                    <div className="ml-auto">
                      <Counter
                        maxValue={upDisabled ? count : 9}
                        minValue={downDisabled ? count : 0}
                        name={`vehicleCount-${vehicleType.code}`}
                        onChange={(value) => onSelectVehicle(vehicleType.code, value, vehicleType.categoryCode, selectedVehicle.trailer)}
                        value={count}
                        t={t}
                      />
                    </div>
                  </li>
                )
              })}
            </ul>
          </>
        ) : (
          <>
            <button
              className="flex w-full cursor-pointer flex-row items-center gap-semantic-global-xs border-0 border-b border-solid border-semantic-global-border-light bg-semantic-global-background-white p-semantic-global-md text-left focus-visible:outline-2 focus-visible:-outline-offset-2! focus-visible:outline-semantic-global-primary-default"
              data-e2e="vehicle-categories-selector"
              onClick={() => onSelectCategory(undefined)}
              type="button"
            >
              <ArrowBackIcon className="text-[24px] text-semantic-global-text-default" size="lg" />
              <span className="cursor-pointer text-base font-medium text-semantic-global-text-default">{t('VEHICLE-CATEGORIES')}</span>
            </button>
            <ul
              aria-label={t('VEHICLE')}
              className="m-0 list-none border-0 border-solid pl-0"
              data-list
              data-travel-search-child
              role="listbox"
            >
              {vehicleTypesInCategory.map((vehicleType, index) => (
                <li
                  tabIndex={index === rovingVehicleIndex ? 0 : -1}
                  data-list-option
                  role="option"
                  aria-selected={vehicleType.code === selectedVehicle?.code}
                  onClick={() => {
                    onSelectVehicle(vehicleType.code, 1, selectedCategoryCode)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onSelectVehicle(vehicleType.code, 1, selectedCategoryCode)
                    }
                    handleListNavigation(event)
                  }}
                  className={clsx(
                    `m-0 list-none flex h-[90px] w-full cursor-pointer items-center gap-semantic-global-md border-0 border-b border-solid border-semantic-global-border-light p-0 px-semantic-global-md text-left focus-visible:z-10 focus-visible:outline-2 focus-visible:-outline-offset-2! focus-visible:outline-semantic-global-primary-default`,
                    vehicleType.code === selectedVehicle?.code
                      ? 'bg-semantic-form-controls-background-ghost-pressed'
                      : `bg-semantic-global-background-white lg:hover:bg-semantic-form-controls-background-ghost-hover`
                  )}
                  data-e2e={`vehicle-type-${vehicleType.code}`}
                  key={vehicleType.code}
                >
                  <div className="min-w-[46px] max-w-[46px] cursor-pointer text-semantic-global-text-default">
                    <Dimensions height={vehicleType.height} length={vehicleType.length}>
                      <VehicleIcon code={vehicleType.code} size="lg" />
                    </Dimensions>
                  </div>
                  <div className="flex flex-col">
                    <span className="cursor-pointer text-base font-medium text-semantic-global-text-default">
                      {t(getVehicleResourceKey(vehicleType.code))}
                    </span>
                    <div className="cursor-pointer text-sm text-semantic-global-text-light">
                      {t(`VEHICLE-DESCRIPTION-${vehicleType.code.replace('RES', '')}`)}
                    </div>
                  </div>
                  {vehicleType.code === selectedVehicle?.code ? (
                    <div className="ml-auto text-semantic-global-text-default">
                      <YesIcon size="md" />
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        )
      ) : null}

      {selectedCategoryCode ? (
        <>
          <div className="bg-semantic-global-background-white p-semantic-global-md">
            <div className="font-medium text-semantic-global-text-default">{t('TRAILER-CARAVAN')}</div>
            <div
              className={`mt-semantic-global-2xs border border-solid border-semantic-global-primary-darkest bg-semantic-global-background-white ${
                selectedTrailerNoLongerAvailable && 'border-semantic-global-danger-default'
              }`}
              onBlur={(event) => {
                if (!showTrailerSelector || event.currentTarget.contains(event.relatedTarget)) return
                setShowTrailerSelector(false)
              }}
            >
              <button
                aria-controls="trailer-options"
                aria-expanded={showTrailerSelector}
                className="flex w-full cursor-pointer flex-row border-0 bg-semantic-global-background-white p-semantic-global-xs text-left focus-visible:outline-2 focus-visible:-outline-offset-2! focus-visible:outline-semantic-global-primary-default"
                data-e2e="trailer-selector"
                onClick={() => {
                  setShowTrailerSelector(!showTrailerSelector)
                  if (isMobile || isTablet) {
                    setTimeout(() => {
                      trailerDropdownRef.current?.firstElementChild?.scrollIntoView({ behavior: 'smooth' })
                    }, 1)
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
                  event.preventDefault()
                  const position = event.key === 'ArrowDown' ? 'first' : 'last'
                  if (showTrailerSelector) {
                    focusTrailerOption(position)
                    return
                  }
                  pendingTrailerFocusRef.current = position
                  setShowTrailerSelector(true)
                }}
                ref={trailerDropdownRef}
                type="button"
              >
                <div className="flex flex-row items-center gap-semantic-global-xs font-medium text-semantic-global-text-default">
                  <TrailerIcon code={selectedVehicle.trailer?.code} />
                  <span data-e2e-sel-trailer={selectedVehicle.trailer ? selectedVehicle.trailer.code : 'NO-TRAILER'}>
                    {selectedVehicle.trailer
                      ? `${t(getVehicleResourceKey(selectedVehicle.trailer.code))} < ${selectedVehicle.trailer.height}m x ${
                          selectedVehicle.trailer.length
                        }m`
                      : t('NO-TRAILER-CARAVAN')}
                  </span>
                </div>
                <div className={clsx('ml-auto transition-transform duration-200', showTrailerSelector ? '-rotate-180' : 'rotate-0')}>
                  <ChevronDownIcon size="md" />
                </div>
              </button>
              {showTrailerSelector ? (
                <ul
                  aria-label={t('TRAILER-CARAVAN')}
                  className="m-0 list-none border-0 border-solid p-0 pl-0"
                  data-list
                  data-travel-search-child
                  id="trailer-options"
                  onKeyDown={(event) => {
                    if (event.key !== 'Escape') return
                    // Keep Escape scoped to this dropdown so the vehicle panel stays open.
                    event.stopPropagation()
                    closeTrailerSelector()
                  }}
                  role="listbox"
                >
                  <li className="m-0 list-none" data-e2e="no-trailer" key="no-trailer">
                    <button
                      aria-selected={!selectedVehicle.trailer}
                      className={clsx(
                        `flex w-full cursor-pointer flex-row items-center gap-semantic-global-xs border-0 border-b border-t border-solid border-semantic-global-border-light p-0 px-semantic-global-xs py-semantic-global-xs text-left focus-visible:z-10 focus-visible:outline-2 focus-visible:-outline-offset-2! focus-visible:outline-semantic-global-primary-default`,
                        !selectedVehicle.trailer
                          ? 'bg-semantic-form-controls-background-ghost-pressed'
                          : `bg-semantic-global-background-white lg:hover:bg-semantic-form-controls-background-ghost-hover`
                      )}
                      data-list-option
                      onClick={() => {
                        onSelectTrailer(undefined)
                        closeTrailerSelector()
                      }}
                      onKeyDown={handleListNavigation}
                      role="option"
                      tabIndex={rovingTrailerIndex === 0 ? 0 : -1}
                      type="button"
                    >
                      <NoIcon size="md" />
                      <div className="font-medium text-semantic-global-text-default">{t('NO-TRAILER-CARAVAN')}</div>
                      {!selectedVehicle.trailer?.code ? (
                        <div className="ml-auto text-semantic-global-text-default">
                          <YesIcon size="md" />
                        </div>
                      ) : null}
                    </button>
                  </li>
                  {availableTrailers.map((trailer, index) => (
                    <li className="m-0 list-none" data-e2e={`trailer-${trailer.code}`} key={trailer.code}>
                      <button
                        aria-selected={trailer.code === selectedVehicle.trailer?.code}
                        className={`flex w-full cursor-pointer flex-row items-center gap-semantic-global-xs border-0 border-b border-solid border-semantic-global-border-light p-0 px-semantic-global-xs py-semantic-global-xs text-left focus-visible:z-10 focus-visible:outline-2 focus-visible:-outline-offset-2! focus-visible:outline-semantic-global-primary-default ${
                          selectedVehicle.trailer === trailer
                            ? 'bg-semantic-form-controls-background-ghost-pressed'
                            : `bg-semantic-global-background-white lg:hover:bg-semantic-form-controls-background-ghost-hover`
                        } `}
                        data-list-option
                        onClick={() => {
                          onSelectTrailer(trailer)
                          closeTrailerSelector()
                        }}
                        onKeyDown={handleListNavigation}
                        role="option"
                        tabIndex={rovingTrailerIndex === index + 1 ? 0 : -1}
                        type="button"
                      >
                        <TrailerIcon code={trailer.code} />
                        <div className="font-medium text-semantic-global-text-default">
                          {`${t(getVehicleResourceKey(trailer.code))} <`} {trailer.height}m x {trailer.length}m
                        </div>
                        {trailer.code === selectedVehicle.trailer?.code ? (
                          <div className="ml-auto text-semantic-global-text-default">
                            <YesIcon size="md" />
                          </div>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            {selectedTrailerNoLongerAvailable ? (
              <label className="bg-semantic-global-background-white text-xs font-light text-semantic-global-danger-default">
                <span data-e2e="incompatible-trailer">
                  {t('INCOMPATIBLE-CAR-TRAILER-ERROR-MESSAGE', {
                    trailerType: `${t(getVehicleResourceKey(selectedTrailerNoLongerAvailable.code))} < ${
                      selectedTrailerNoLongerAvailable.height
                    }m x ${selectedTrailerNoLongerAvailable.length}m`,
                    vehicleType: t(getVehicleResourceKey(selectedVehicle?.code)),
                  })}
                </span>
              </label>
            ) : null}
          </div>
          <div className="border-t border-semantic-global-border-light">
            <div className="flex flex-row items-center gap-semantic-global-md p-semantic-global-md">
              <div className="flex flex-col">
                <span id="multiple-vehicles-toggle-label" className="font-medium text-semantic-global-text-default">
                  {t('MULTIPLE-VEHICLES-TITLE')}
                </span>
                {vehicleTypesInCategory?.length === 0 || multipleVehiclesSupported ? (
                  <div
                    className="mt-semantic-global-xs text-sm font-normal text-semantic-global-text-default"
                    data-e2e="multiple-vehicles-description"
                  >
                    {t('MULTIPLE-VEHICLES-DESCRIPTION')}
                  </div>
                ) : (
                  <div
                    className="mt-semantic-global-xs text-sm font-normal text-semantic-global-text-default"
                    data-e2e="multiple-vehicles-error-description"
                  >
                    {selectedVehicle?.code === 'NCAR'
                      ? t('MULTIPLE-VEHICLES-SELECT-VEHICLE-DESCRIPTION')
                      : t('MULTIPLE-VEHICLES-NOT-SUPPORTED-DESCRIPTION')}
                  </div>
                )}
              </div>
              <div className="ml-auto">
                <Switch
                  aria-labelledby="multiple-vehicles-toggle-label"
                  checked={multipleVehiclesChecked}
                  disabled={!multipleVehiclesSupported || vehicleTypesInCategory?.length === 0}
                  name="multiple-vehicles"
                  onChange={onCheckMultipleVehicle}
                />
              </div>
            </div>
          </div>
        </>
      ) : null}
    </>
  )
}

export default Selector

export type VehicleNumber = {
  value: number
}
